import { DecreeSingleSelect } from "@/components/form-control/DecreeSingleSelect";
import { DecreeCategory, RestrictionType } from "@/types/gql/graphql";
import * as React from "react";
import * as z from "zod";

import { DropdownSelect } from "@/components/form-control";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { calculateArea, getPlace } from "@/helper/format-data";
import { isNil } from "@/helper/utils";
import { Restriction } from "@/types";
import { Feature, LineString } from "geojson";
import { useEffect, useMemo } from "react";
import { RestrictionModalProps } from "types/restriction.type";

import { InputNumber } from "@/components/form-control/input-number";
import { TimeSpanListing } from "@/components/time-span";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DaysOfWeek } from "@/types/gql/graphql";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { trafficRestrictionTypeList } from "../restriction.const";

const restrictionSchema = z.object({
  timeSpans: z
    .array(
      z.object({
        startDate: z.date({ message: "La date de début est requise" }),
        endDate: z.date({ message: "La date de fin est requise" }),
        daysOfWeek: z.array(z.enum(DaysOfWeek)).optional(),
        daysOfMonth: z.array(z.number()).optional(),
        months: z.array(z.number()).optional(),
        timeOfDayStart: z.string().optional().nullable(),
        timeOfDayEnd: z.string().optional().nullable(),
      }),
    )
    .min(1, "Une plage horaire est requise"),
  streetName: z.string().min(1, "Le nom de la rue est requis"),
  streetNumberStart: z.string().optional().nullable(),
  streetNumberEnd: z.string().optional().nullable(),
  selectedDecree: z.object({ value: z.string(), label: z.string() }, { message: "L'arrêté est requis" }),
  description: z.string().min(1, "La description est requise"),
  type: z.enum(RestrictionType).optional().nullable(),
  calculatedSpaces: z.number().optional(),
  width: z.number().min(1, "La largeur doit être d'au moins 1"),
});

type RestrictionFormValues = z.infer<typeof restrictionSchema>;

export const RestrictionModal = ({ saveRestriction, prefill, cancel }: RestrictionModalProps) => {
  const firstDecree = prefill.decrees?.[0];
  const selectedDecree = firstDecree ? { value: firstDecree.id, label: firstDecree.name } : undefined;
  const form = useForm<RestrictionFormValues>({
    resolver: zodResolver(restrictionSchema),
    mode: "all",
    defaultValues: {
      timeSpans:
        prefill.timeSpans?.map((item) => ({
          ...item,
          daysOfMonth: item.daysOfMonth ?? [],
          daysOfWeek: item.daysOfWeek ?? [],
          months: item.months ?? [],
          startDate: item.startDate ? new Date(item.startDate) : new Date(),
          endDate: item.endDate ? new Date(item.endDate) : new Date(),
        })) ?? [],
      streetName: prefill.streetName ?? "",
      description: prefill.description ?? "",
      width: prefill?.width ?? 2,
      streetNumberStart: prefill.streetNumberStart ?? "",
      streetNumberEnd: prefill.streetNumberEnd ?? "",
      type: prefill.type,
      calculatedSpaces: prefill.calculatedSpaces ?? 0,
      selectedDecree,
    },
  });

  const { setValue, control } = form;

  const getValueSpaces = React.useCallback(
    (value = 2) => {
      const curbZoneFeature = {
        type: "Feature",
        geometry: prefill.editLine.geometry,
        properties: { ...prefill, width: Number(value) },
      } as Feature<LineString, Restriction>;
      const { calculatedSpaces } = calculateArea(curbZoneFeature);
      return calculatedSpaces;
    },
    [prefill],
  );

  useEffect(() => {
    (async () => {
      if (!prefill?.streetName) {
        const newLineParking = await getPlace(prefill.editLine);
        setValue("streetName", newLineParking.properties.streetName ?? "");
        setValue("streetNumberStart", newLineParking.properties.streetNumberStart);
        setValue("streetNumberEnd", newLineParking.properties.streetNumberEnd);
      }
      if (!prefill.calculatedSpaces) {
        const newCalculatedSpaces = getValueSpaces(prefill?.width ?? undefined);
        setValue("calculatedSpaces", newCalculatedSpaces);
      }
    })();
  }, [getValueSpaces, prefill, setValue]);

  const isEdit = !isNil(prefill.id);

  const dismiss = () => cancel(isEdit);
  const save = (promptedProperties: RestrictionFormValues) => {
    saveRestriction({
      ...prefill,
      ...promptedProperties,
      decreeIds: [promptedProperties.selectedDecree.value],
      type: promptedProperties.type,
      timeSpans: promptedProperties.timeSpans.map((span) => ({
        ...span,
        startDate: span.startDate.toISOString(),
        endDate: span.endDate.toISOString(),
      })),
    });
  };

  const isCurbZoneRestriction = useMemo(() => {
    return !!prefill.curbZoneId;
  }, [prefill]);

  const headerText = useMemo(() => {
    if (isCurbZoneRestriction) {
      return isEdit ? "Modifier une restriction" : "Ajouter une restriction";
    }
    return isEdit ? "Modifier une restriction de circulation" : "Ajouter une restriction de circulation";
  }, [isCurbZoneRestriction, isEdit]);

  const calculateCurbZoneRestrictionSpaces = (value: number) => {
    const newValue = getValueSpaces(value);
    setValue("calculatedSpaces", newValue);
  };
  return (
    <Dialog open>
      <DialogContent className="h-[calc(100vh-5em)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{headerText}</DialogTitle>
          <DialogDescription>Informations sur la restriction</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(save)} className="space-y-6">
            <FormField
              control={control}
              name="timeSpans"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>
                    Plages horaires <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <div>
                      <TimeSpanListing
                        timeSpans={value}
                        setTimeSpans={onChange}
                        options={{ showSelectDate: true, showTimeOfDay: true }}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="streetName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Nom de la rue <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="streetNumberStart"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>Numéro de début</FormLabel>
                  <FormControl>
                    <Input value={value ?? undefined} onChange={onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="streetNumberEnd"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>Numéro de fin</FormLabel>
                  <FormControl>
                    <Input value={value ?? undefined} onChange={onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DecreeSingleSelect categories={[DecreeCategory.Temporary]} required />

            <FormField
              control={control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Description <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isCurbZoneRestriction && (
              <FormField
                control={control}
                name="type"
                render={({ field: { onChange, value } }) => (
                  <FormItem>
                    <FormLabel>
                      Type<span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <DropdownSelect
                        options={trafficRestrictionTypeList}
                        value={value ?? undefined}
                        onValueChange={onChange}
                        className="w-full"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={control}
              name="calculatedSpaces"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Places calculées</FormLabel>
                  <FormControl>
                    <InputNumber {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="width"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>
                    Largeur <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <InputNumber
                      value={value}
                      onChange={(newValue) => {
                        onChange(newValue);
                        calculateCurbZoneRestrictionSpaces(newValue);
                      }}
                      min={1}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="sm:justify-between">
              <Button onClick={dismiss} variant="secondary" type="button">
                Annuler
              </Button>
              <Button type="submit">Enregistrer</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
